import {
  Award,
  BookOpen,
  ChartNoAxesCombined,
  ClipboardCheck,
  Gift,
  GitBranch,
  LogOut,
  MessageCircle,
  Palette,
  Settings2,
  Users,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { NavLink, Outlet } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, authenticatedFileUrl } from "@/lib/api";
import { brandingAssetUrl, useBranding } from "@/hooks/useBranding";
import { SupportInboxWidget } from "@/components/SupportInboxWidget";
import { StudentContactModal } from "@/components/StudentContactModal";

const baseNav = {
  student: [
    { to: "/", label: "Pulpit", icon: ChartNoAxesCombined },
    { to: "/kursy", label: "Kursy", icon: BookOpen },
    { to: "/zadania", label: "Zadania", icon: ClipboardCheck },
    { to: "/bonusy", label: "Bonusy", icon: Gift },
    {
      to: "/spolecznosc",
      label: "Społeczność",
      icon: MessageCircle,
      feature: "community",
    },
    {
      to: "/certyfikaty",
      label: "Certyfikaty",
      icon: Award,
      feature: "certificates",
    },
  ],
  curator: [
    { to: "/", label: "Pulpit", icon: ChartNoAxesCombined },
    { to: "/kursy", label: "Kursy", icon: BookOpen },
    { to: "/zadania", label: "Prace", icon: ClipboardCheck },
  ],
  admin: [
    { to: "/", label: "Pulpit", icon: ChartNoAxesCombined },
    { to: "/kursy", label: "Produkty", icon: BookOpen },
    { to: "/zadania", label: "Zadania", icon: ClipboardCheck },
    { to: "/uzytkownicy", label: "Użytkownicy", icon: Users },
    { to: "/wyglad", label: "Wygląd", icon: Palette },
    { to: "/integracje", label: "Integracje", icon: Settings2 },
    { to: "/lejki", label: "Lejki", icon: GitBranch },
  ],
};

const mobileNavByRole = {
  student: baseNav.student.slice(0, 4),
  curator: baseNav.curator,
  admin: [baseNav.admin[0], baseNav.admin[1], baseNav.admin[3], baseNav.admin[6], baseNav.admin[5]],
};

export const TopAppShell = () => {
  const { user, logout } = useAuth();
  const { data: branding } = useBranding();
  const [features, setFeatures] = useState({
    community: true,
    certificates: true,
  });
  const [studentHeader, setStudentHeader] = useState({ course_id: null, unread: 0 });
  const [questionOpen, setQuestionOpen] = useState(false);
  const markStudentMessagesRead = useCallback(() => {
    setStudentHeader((current) => current.unread ? { ...current, unread: 0 } : current);
  }, []);

  useEffect(() => {
    if (user.role !== "student") return;
    const load = () => api.get("/student/header").then(({ data }) => {
      setFeatures(data.features);
      setStudentHeader({ course_id: data.course_id, unread: data.unread || 0 });
    }).catch(() => {});
    load();
    const timer = window.setInterval(load, 30000);
    return () => window.clearInterval(timer);
  }, [user.role]);

  const nav = baseNav[user.role].filter(
    (item) => !item.feature || features[item.feature],
  );
  const mobileNav = mobileNavByRole[user.role].filter(
    (item) => !item.feature || features[item.feature],
  );
  const roleLabel = {
    student: "Uczeń",
    curator: "Kurator",
    admin: "Administrator",
  }[user.role];
  const initials = user.name
    .trim()
    .split(/\s+/)
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
  const avatarUrl = user.avatar_file_id
    ? authenticatedFileUrl(user.avatar_file_id)
    : "";
  const avatar = avatarUrl ? <img src={avatarUrl} alt="" /> : initials;
  const logoUrl = brandingAssetUrl(branding?.logo_file_id) || "/marsem-logo.webp";

  return (
    <div className="top-shell">
      <header className="premium-topbar" data-testid="premium-top-navigation">
        <NavLink className={`top-logo ${branding?.logo_file_id ? "custom" : ""}`} to="/" data-testid="top-logo-link">
          <img src={logoUrl} alt="MARSEM LAB" />
        </NavLink>
        <nav data-testid="top-navigation-links">
          {nav.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              end={to === "/"}
              data-testid={`top-nav-${label.toLowerCase()}`}
            >
              <Icon />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="top-profile" data-testid="top-user-profile">
          {user.role === "admin" ? <SupportInboxWidget /> : null}
          {user.role === "student" ? <button className="student-question-trigger" onClick={() => setQuestionOpen(true)} aria-label={`Masz pytanie? ${studentHeader.unread} nieprzeczytanych odpowiedzi`} data-testid="header-question-button"><MessageCircle/><span>Masz pytanie?</span>{studentHeader.unread ? <b>{studentHeader.unread > 99 ? "99+" : studentHeader.unread}</b> : null}</button> : null}
          <div>
            <strong>{user.name}</strong>
            <small>{roleLabel}</small>
          </div>
          <NavLink
            className="top-profile-avatar"
            to="/profil"
            aria-label="Ustawienia profilu"
          >
            {avatar}
          </NavLink>
          <button
            onClick={logout}
            title="Wyloguj"
            data-testid="top-logout-button"
          >
            <LogOut />
          </button>
        </div>
        {user.role === "student" ? <button className="mobile-question-trigger" onClick={() => setQuestionOpen(true)} aria-label={`Masz pytanie? ${studentHeader.unread} nieprzeczytanych odpowiedzi`} data-testid="mobile-header-question-button"><MessageCircle/><span>Pytanie</span>{studentHeader.unread ? <b>{studentHeader.unread > 99 ? "99+" : studentHeader.unread}</b> : null}</button> : null}
        <NavLink
          className="top-profile-trigger"
          to="/profil"
          aria-label="Ustawienia profilu"
          data-testid="top-profile-trigger"
        >
          {avatar}
        </NavLink>
      </header>
      <main className="top-main">
        <Outlet />
      </main>
      {mobileNav.length > 0 && (
        <nav
          className="mobile-bottom-nav"
          style={{ "--mobile-nav-items": mobileNav.length }}
          data-testid="mobile-bottom-navigation"
        >
          {mobileNav.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              end={to === "/"}
              data-testid={`mobile-nav-${label.toLowerCase()}`}
            >
              <Icon />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>
      )}
      {user.role === "student" ? <StudentContactModal open={questionOpen} onClose={() => setQuestionOpen(false)} type="question" courseId={studentHeader.course_id} onMessagesRead={markStudentMessagesRead}/> : null}
    </div>
  );
};
